/**
 * Luxe Drawing Symbol Registry — Phase 1A-S.
 *
 * Central store for electrical & lighting drawing symbols. All symbols
 * are Luxe-authored SVG geometry that follow common Australian drafting
 * conventions — they are NOT direct copies of any AS/NZS or SAI Global
 * standard artwork. When a project later needs to be issued to a
 * specific standard (e.g. AS/NZS 3000:2018), the studio should confirm
 * that the client accepts the Luxe generic representation.
 *
 * References consulted while authoring these symbols (Feb 2026):
 *   • AS/NZS 3000:2018 "Electrical installations" — text-based rules,
 *     no graphical symbol library.
 *   • ISO 7010, IEC 60417 — general graphical symbols for equipment
 *     (informative only for our use).
 *   • AS 1101 series — public-facing conventions inspiration.
 *
 * A dedicated licensing pass is required before shipping any symbol
 * that reproduces protected artwork; the Luxe generic set below sits
 * comfortably outside that scope.
 *
 * Each entry:
 *   type     — canonical DesignScene symbol_type
 *   legend   — legend code that appears on future documentation
 *   description — human-readable legend description
 *   svg(px)  — returns an SVG element rendered at the fixed pixel size
 */

// eslint-disable-next-line react/prop-types
const G = ({ children, ...rest }) => <g strokeLinecap="round" strokeLinejoin="round" {...rest}>{children}</g>;

export const DRAWING_SYMBOLS = {
  gpo_single: {
    legend: "GPO",
    description: "Single general purpose socket-outlet (240 V)",
    render: (px = 22) => (
      // Australian architectural convention: small filled circle representing
      // the outlet body with a short perpendicular lead-line back to the wall,
      // and a single index tick marking the switched pole. This form is used
      // on Australian architectural plans to distinguish a single outlet from
      // a double outlet without relying on text.
      <svg width={px} height={px} viewBox="0 0 24 24" data-testid="sym-gpo-single">
        <g strokeLinecap="round" strokeLinejoin="round" stroke="#111827" strokeWidth="1.6" fill="#FEF3C7">
          <line x1="12" y1="20" x2="12" y2="16" stroke="#111827" strokeWidth="1.6"/>
          <circle cx="12" cy="10" r="6"/>
          <line x1="12" y1="4" x2="12" y2="7" stroke="#111827" strokeWidth="1.6"/>
        </g>
      </svg>
    ),
  },
  gpo_double: {
    legend: "GPO2",
    description: "Double general purpose socket-outlet (240 V)",
    render: (px = 30) => (
      // Australian architectural convention for a double outlet: TWO circles
      // sharing a single lead-line back to the wall, with a single switched
      // tick between them. Immediately distinguishable from a single outlet
      // by outline count — no reliance on text.
      <svg width={px} height={px * 0.75} viewBox="0 0 34 24" data-testid="sym-gpo-double">
        <g strokeLinecap="round" strokeLinejoin="round" stroke="#111827" strokeWidth="1.6" fill="#FEF3C7">
          <line x1="17" y1="20" x2="17" y2="17" stroke="#111827" strokeWidth="1.6"/>
          <circle cx="10" cy="11" r="6"/>
          <circle cx="24" cy="11" r="6"/>
          <line x1="17" y1="5" x2="17" y2="8" stroke="#111827" strokeWidth="1.6"/>
        </g>
      </svg>
    ),
  },
  data_outlet: {
    legend: "D",
    description: "Data / RJ-45 outlet",
    render: (px = 22) => (
      <svg width={px} height={px} viewBox="0 0 24 24" data-testid="sym-data">
        <G stroke="#1D4ED8" strokeWidth="1.6" fill="#DBEAFE">
          <rect x="4" y="6" width="16" height="12" rx="1.5"/>
          <text x="12" y="15" textAnchor="middle" fontSize="8" fill="#1D4ED8" stroke="none">D</text>
        </G>
      </svg>
    ),
  },
  switch_single: {
    legend: "S",
    description: "Single one-way light switch",
    render: (px = 20) => (
      <svg width={px} height={px} viewBox="0 0 24 24" data-testid="sym-switch-single">
        <G stroke="#111827" strokeWidth="1.6" fill="none">
          <circle cx="12" cy="12" r="4"/>
          <line x1="12" y1="8" x2="16" y2="4"/>
        </G>
      </svg>
    ),
  },
  switch_multi: {
    legend: "S/n",
    description: "Multi-gang light switch (n = number of gangs)",
    render: (px = 22) => (
      <svg width={px} height={px} viewBox="0 0 28 24" data-testid="sym-switch-multi">
        <G stroke="#111827" strokeWidth="1.6" fill="none">
          <circle cx="14" cy="12" r="4"/>
          <line x1="14" y1="8" x2="18" y2="4"/>
          <line x1="14" y1="8" x2="20" y2="6"/>
          <line x1="14" y1="8" x2="22" y2="8"/>
        </G>
      </svg>
    ),
  },
  dimmer: {
    legend: "SD",
    description: "Rotary / touch dimmer",
    render: (px = 20) => (
      <svg width={px} height={px} viewBox="0 0 24 24" data-testid="sym-dimmer">
        <G stroke="#111827" strokeWidth="1.6" fill="none">
          <circle cx="12" cy="12" r="6"/>
          <line x1="12" y1="12" x2="18" y2="8"/>
          <text x="20" y="7" fontSize="7" fill="#111827" stroke="none">D</text>
        </G>
      </svg>
    ),
  },
  downlight: {
    legend: "DL",
    description: "Recessed ceiling downlight",
    render: (px = 22) => (
      <svg width={px} height={px} viewBox="0 0 24 24" data-testid="sym-downlight">
        <G stroke="#B45309" strokeWidth="1.6" fill="#FEF3C7">
          <circle cx="12" cy="12" r="8"/>
          <circle cx="12" cy="12" r="3" fill="#FBBF24" stroke="#B45309"/>
        </G>
      </svg>
    ),
  },
  wall_light: {
    legend: "WL",
    description: "Wall-mounted decorative luminaire",
    render: (px = 20) => (
      <svg width={px} height={px} viewBox="0 0 24 24" data-testid="sym-wall-light">
        <G stroke="#B45309" strokeWidth="1.6" fill="#FEF3C7">
          <path d="M6 18 h12"/>
          <path d="M12 4 L6 14 h12 z"/>
        </G>
      </svg>
    ),
  },
};

export function symbolFor(type) {
  return DRAWING_SYMBOLS[type] || null;
}

// List for the Legend on future construction plans.
export function legendEntries() {
  return Object.entries(DRAWING_SYMBOLS).map(([type, s]) => ({
    type, legend: s.legend, description: s.description,
  }));
}
